import React, { useMemo, useState } from 'react';
import {
  Sparkles,
  MessageSquare,
  ScanFace,
  Activity,
  Clapperboard,
  LucideIcon,
} from 'lucide-react';

interface ClipDescriptionProps {
  description?: string | null;
  collapseThreshold?: number;
}

interface Insight {
  label: string;
  detail: string;
  icon: LucideIcon;
  accent: string;
}

const insightDescriptors: Array<{
  match: RegExp;
  label: string;
  icon: LucideIcon;
  accent: string;
}> = [
  {
    match: /speech highlight/i,
    label: 'Speech Highlight',
    icon: MessageSquare,
    accent: 'text-red-600',
  },
  {
    match: /face activity/i,
    label: 'Face Activity',
    icon: ScanFace,
    accent: 'text-gray-600',
  },
  {
    match: /scene (transition|change)/i,
    label: 'Scene Change',
    icon: Clapperboard,
    accent: 'text-gray-700',
  },
  {
    match: /sentiment|emotion/i,
    label: 'Sentiment Shift',
    icon: Activity,
    accent: 'text-red-500',
  },
];

const DEFAULT_COLLAPSE_THRESHOLD = 4;

const parseInsights = (description?: string | null): Insight[] => {
  if (!description) {
    return [];
  }

  const segments = description
    .split(/\s*\+\s*/g)
    .map((segment) => segment.trim())
    .filter(Boolean);

  return segments.map((segment) => {
    const [rawLabel, ...rest] = segment.split(':');
    const detail = rest.join(':').trim() || segment;
    const descriptor =
      insightDescriptors.find((item) => item.match.test(rawLabel)) || null;

    const label =
      descriptor?.label || (rawLabel ? rawLabel.trim() : 'Highlight Insight');

    return {
      label,
      detail: detail || label,
      icon: descriptor?.icon || Sparkles,
      accent: descriptor?.accent || 'text-gray-500',
    };
  });
};

const ClipDescription: React.FC<ClipDescriptionProps> = ({
  description,
  collapseThreshold = DEFAULT_COLLAPSE_THRESHOLD,
}) => {
  const [expanded, setExpanded] = useState(false);
  const insights = useMemo(() => parseInsights(description), [description]);
  const visibleInsights = expanded
    ? insights
    : insights.slice(0, collapseThreshold);

  if (!description || !description.trim()) {
    return (
      <p className="text-gray-400 italic">
        No AI description generated for this clip yet.
      </p>
    );
  }

  if (!insights.length) {
    return <p className="text-gray-900">{description}</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Sparkles className="h-4 w-4 text-gray-500" />
          <span>AI Clip Insights</span>
        </div>
        {insights.length > collapseThreshold && (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
          >
            {expanded ? 'Hide details' : 'Show all insights'}
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {visibleInsights.map((insight, index) => {
          const Icon = insight.icon;
          return (
            <div
              key={`${insight.label}-${index}`}
              className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
            >
              <Icon className={`h-4 w-4 mt-0.5 ${insight.accent}`} />
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {insight.label}
                </p>
                <p className="text-xs text-gray-600 leading-relaxed">
                  {insight.detail}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {!expanded && insights.length > collapseThreshold && (
        <p className="text-xs text-gray-500">
          Showing the first {visibleInsights.length} of {insights.length} AI
          highlights
        </p>
      )}
    </div>
  );
};

export default ClipDescription;


